import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Users, Wallet, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';
import { CandidateDialog } from './CandidateDialog';
import { StaffCostOverview, money } from './StaffCostOverview';

const TONE = { candidate: 'bg-sky-50 text-sky-700 border-sky-200', joined: 'bg-emerald-50 text-emerald-700 border-emerald-200', withdrawn: 'bg-gray-100 text-gray-500 border-gray-200' };
const LABEL = { candidate: 'In process', joined: 'Joined', withdrawn: 'Withdrawn' };
const VIEWS = [['candidates', 'Candidates', Users], ['overview', 'Staff cost to company', Wallet]];

export default function HiringCostsTab() {
  const [meta, setMeta] = useState({ can_edit: null, descriptions: [], employees: [] });
  const [data, setData] = useState({ rows: [], total: 0 });
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [dq, setDq] = useState('');
  useEffect(() => { const t = setTimeout(() => setDq(q), 300); return () => clearTimeout(t); }, [q]);
  const [edit, setEdit] = useState(null);
  const [view, setView] = useState('candidates');
  const load = useCallback(() => api.get('/api/hiring-costs', { params: { status, q: dq } }).then((r) => setData(r.data)).catch(() => {}), [status, dq]);
  useEffect(() => { api.get('/api/hiring-costs/meta').then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (r) => {
    if (!window.confirm(`Delete hiring costs for ${r.candidate_name}?`)) return;
    try { await api.delete(`/api/hiring-costs/${r.id}`); load(); } catch (e) { toast({ title: 'Error', description: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="space-y-4" data-testid="hiring-costs-tab">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold text-gray-900 mr-2">Hiring Costs</h2>
        {meta.can_edit === false && <Badge variant="outline" className="text-xs"><Lock className="h-3 w-3 mr-1" />View only</Badge>}
        {meta.can_edit && view === 'candidates' && <Button size="sm" className="ml-auto" onClick={() => setEdit({})} data-testid="hc-new-btn"><Plus className="h-4 w-4 mr-1" />New candidate</Button>}
      </div>
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="hc-subviews">
        {VIEWS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`hc-view-${k}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${view === k ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-800'}`}><Icon className="h-4 w-4" />{l}</button>
        ))}
      </div>
      {view === 'overview' ? <StaffCostOverview /> : (<>
        <div className="flex flex-wrap items-center gap-2">
          <Input className="h-9 w-64" placeholder="Search candidate, position, agency…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="hc-search" />
          {['', 'candidate', 'joined', 'withdrawn'].map((s) => (
            <button key={s || 'all'} onClick={() => setStatus(s)} data-testid={`hc-filter-${s || 'all'}`}
              className={`text-xs px-2.5 py-1 rounded-full border ${status === s ? 'bg-violet-100 text-violet-700 border-violet-300 font-semibold' : 'bg-white text-gray-500'}`}>{s ? LABEL[s] : 'All'}</button>
          ))}
          <span className="ml-auto text-sm">Total hiring spend <b data-testid="hc-grand-total">{money(data.total)}</b></span>
        </div>
        <div className="border rounded-lg bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="hc-table">
            <thead className="bg-slate-50 text-[11px] uppercase text-gray-500">
              <tr>{['Candidate', 'Position', 'Source', 'Status', 'Cost breakdown', 'Total', ''].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y">
              {data.rows.map((r) => (
                <tr key={r.id} className="align-top" data-testid={`hc-row-${r.id}`}>
                  <td className="px-3 py-2"><p className="font-medium">{r.candidate_name}</p>{r.employee_code && <p className="text-xs text-emerald-700">→ {r.employee_code} · {r.employee_name}</p>}</td>
                  <td className="px-3 py-2 text-gray-700">{r.position}<p className="text-xs text-gray-500">{r.department}</p></td>
                  <td className="px-3 py-2 text-gray-700">{r.source || '—'}</td>
                  <td className="px-3 py-2"><span className={`text-[11px] px-2 py-0.5 rounded border ${TONE[r.status]}`}>{LABEL[r.status]}</span></td>
                  <td className="px-3 py-2 text-xs text-gray-700">{r.items.length ? r.items.map((i) => <p key={i.id}>{i.description}: <b>{Number(i.amount).toFixed(2)}</b>{i.date && <span className="text-gray-400"> · {i.date}</span>}</p>) : <span className="text-gray-400">No costs yet</span>}</td>
                  <td className="px-3 py-2 font-semibold whitespace-nowrap">{money(r.total)}</td>
                  <td className="px-3 py-2">{meta.can_edit && <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEdit(r)} data-testid={`hc-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => remove(r)} data-testid={`hc-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button></div>}</td>
                </tr>
              ))}
              {!data.rows.length && <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-500">No candidates yet. Click “New candidate” to start tracking hiring costs.</td></tr>}
            </tbody>
          </table>
        </div>
      </>)}
      <CandidateDialog open={!!edit} row={edit && edit.id ? edit : null} meta={meta} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
